export type GeoPosition = number[]; // [lon, lat, ...]
export interface GeoPolygonGeometry { type: 'Polygon'; coordinates: GeoPosition[][]; }
export interface GeoMultiPolygonGeometry { type: 'MultiPolygon'; coordinates: GeoPosition[][][]; }
export type GeoGeometry = GeoPolygonGeometry | GeoMultiPolygonGeometry;
export interface GeoFeature {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: GeoGeometry;
}
export interface GeoFeatureCollection {
  type: 'FeatureCollection';
  features: GeoFeature[];
}

export interface GeoBounds {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
}

export function computeBounds(features: GeoFeature[]): GeoBounds {
  let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
  const visitRing = (ring: GeoPosition[]) => {
    for (const [lon, lat] of ring) {
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  };
  for (const f of features) {
    if (f.geometry.type === 'Polygon') {
      f.geometry.coordinates.forEach(visitRing);
    } else {
      f.geometry.coordinates.forEach((poly) => poly.forEach(visitRing));
    }
  }
  return { minLon, maxLon, minLat, maxLat };
}

/**
 * Outer ring of every polygon in a feature (handles MultiPolygon — e.g. a
 * district with an offshore island — as separate rings). Holes (interior
 * rings, index > 0) are intentionally skipped: none of the geometry in
 * this dataset relies on them for a legible shape at report scale, and
 * skipping is simpler than punching holes with jsPDF's fill rule. Flagging
 * this here in case a future boundary file has districts with real holes.
 */
export function extractOuterRings(feature: GeoFeature): GeoPosition[][] {
  if (feature.geometry.type === 'Polygon') {
    return feature.geometry.coordinates.length > 0 ? [feature.geometry.coordinates[0]] : [];
  }
  return feature.geometry.coordinates
    .filter((poly) => poly.length > 0)
    .map((poly) => poly[0]);
}

function perpendicularDistance(pt: GeoPosition, a: GeoPosition, b: GeoPosition): number {
  const [x, y] = pt, [x1, y1] = a, [x2, y2] = b;
  const dx = x2 - x1, dy = y2 - y1;
  if (dx === 0 && dy === 0) return Math.hypot(x - x1, y - y1);
  const t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);
  const px = x1 + t * dx, py = y1 + t * dy;
  return Math.hypot(x - px, y - py);
}

/**
 * Douglas-Peucker simplification. The uploaded boundary file has ~11.6k
 * points across 118 districts — far more detail than a small report-page
 * map can show, and un-simplified it noticeably slows PDF generation and
 * bloats file size. `epsilonDeg` is in decimal degrees; ~0.01 trims detail
 * below roughly 1km, imperceptible at this map's scale.
 */
export function simplifyRing(points: GeoPosition[], epsilonDeg: number): GeoPosition[] {
  if (points.length < 3) return points;
  let maxDist = 0;
  let index = 0;
  const end = points.length - 1;
  for (let i = 1; i < end; i++) {
    const d = perpendicularDistance(points[i], points[0], points[end]);
    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }
  if (maxDist > epsilonDeg) {
    const left = simplifyRing(points.slice(0, index + 1), epsilonDeg);
    const right = simplifyRing(points.slice(index), epsilonDeg);
    return left.slice(0, -1).concat(right);
  }
  return [points[0], points[end]];
}